// HWP 5.0 바이너리 파일 → HWPXDocument 변환
// DocInfo + BodyText 스트림을 파싱하여 CDF IR로 변환

import { inflateSync } from 'fflate';
import { parseOle, type OleFile } from './ole.js';
import {
  parseRecords,
  TAG,
  readUtf16,
  readU16,
  readU32,
  readI32,
  type HwpRecord,
} from './records.js';
import type {
  HWPXDocument,
  DocumentHead,
  DocumentMeta,
  Section,
  Paragraph,
  Run,
  TextRun,
  CharProperty,
  ParaProperty,
  FontFace,
  FontLang,
  Style,
  BorderFill,
  BorderLine,
  BorderLineType,
  BorderWidth,
} from '../../types.js';
import {
  createDefaultHead,
  createDefaultMeta,
  createDefaultSectionDef,
  createDefaultBorderFill,
} from '../../defaults.js';
import { HWPXError } from '../../errors.js';

export function readHwp5(data: Uint8Array): HWPXDocument {
  const ole = parseOle(data);

  // FileHeader에서 문서 속성 읽기
  const fileHeader = ole.getStream('FileHeader');
  const compressed = isCompressed(fileHeader);
  const hwpVersion = readHwpVersion(fileHeader);

  // DocInfo 파싱
  const docInfoRaw = ole.getStream('DocInfo');
  const docInfoData = compressed ? tryDecompress(docInfoRaw) : docInfoRaw;
  const docInfoRecords = parseRecords(docInfoData);
  const head = parseDocInfo(docInfoRecords);

  // BodyText 섹션 파싱
  const sections = parseBodyText(ole, compressed);

  const meta: DocumentMeta = {
    ...createDefaultMeta(),
    hwpVersion,
  };

  return { meta, head, sections, binData: [] };
}

function isCompressed(fileHeader: Uint8Array): boolean {
  if (fileHeader.length < 40) return false;
  const flags = readU32(fileHeader, 36);
  return (flags & 0x01) !== 0;
}

function readHwpVersion(fileHeader: Uint8Array): string {
  if (fileHeader.length < 36) return '5.0.0.0';
  const major = fileHeader[35];
  const minor = fileHeader[34];
  const build = fileHeader[33];
  const revision = fileHeader[32];
  return `${major}.${minor}.${build}.${revision}`;
}

function tryDecompress(data: Uint8Array): Uint8Array {
  try {
    return inflateSync(data);
  } catch {
    return data;
  }
}

// ─── DocInfo 파싱 ───

function parseDocInfo(records: HwpRecord[]): DocumentHead {
  const head = createDefaultHead();
  head.fontFaces = [];
  head.charProperties = [];
  head.paraProperties = [];
  head.styles = [];
  head.borderFills = [];

  let fontId = 0;
  let charPrId = 0;
  let paraPrId = 0;
  let styleId = 0;
  let borderFillId = 1;

  for (const rec of records) {
    switch (rec.tagId) {
      case TAG.FACE_NAME: {
        const face = parseFaceName(rec.data, fontId);
        if (face) {
          head.fontFaces.push(face);
          fontId++;
        }
        break;
      }
      case TAG.CHAR_SHAPE: {
        const cp = parseCharShape(rec.data, charPrId);
        if (cp) {
          head.charProperties.push(cp);
          charPrId++;
        }
        break;
      }
      case TAG.PARA_SHAPE: {
        const pp = parseParaShape(rec.data, paraPrId);
        if (pp) {
          head.paraProperties.push(pp);
          paraPrId++;
        }
        break;
      }
      case TAG.STYLE: {
        const style = parseStyle(rec.data, styleId);
        if (style) {
          head.styles.push(style);
          styleId++;
        }
        break;
      }
      case TAG.BORDER_FILL: {
        const bf = parseBorderFillRecord(rec.data, borderFillId);
        if (bf) {
          head.borderFills.push(bf);
          borderFillId++;
        }
        break;
      }
    }
  }

  // 최소 기본값 보장
  if (head.fontFaces.length === 0) head.fontFaces = [{ lang: 'HANGUL', fontName: '함초롬돋움', fontType: 'ttf' }];
  if (head.charProperties.length === 0) head.charProperties = [{ id: 0, height: 1000, textColor: 0, fontRef: { hangul: 0, latin: 0, hanja: 0, japanese: 0, other: 0, symbol: 0, user: 0 } }];
  if (head.paraProperties.length === 0) head.paraProperties = [{ id: 0, alignment: 'JUSTIFY', lineSpacing: { type: 'PERCENT', value: 160 }, paraMargin: { left: 0, right: 0, indent: 0, prevSpacing: 0, nextSpacing: 0 } }];
  if (head.styles.length === 0) head.styles = [{ id: 0, type: 'PARA', name: '바탕글', engName: 'Normal', paraPrIDRef: 0, charPrIDRef: 0 }];
  if (head.borderFills.length === 0) head.borderFills = [createDefaultBorderFill(1)];

  return head;
}

function parseFaceName(data: Uint8Array, id: number): FontFace | null {
  if (data.length < 6) return null;

  const nameLen = readU16(data, 2);
  if (data.length < 4 + nameLen * 2) return null;

  const fontName = readUtf16(data, 4, nameLen);
  const langMap: FontLang[] = ['HANGUL', 'LATIN', 'HANJA', 'JAPANESE', 'OTHER', 'SYMBOL', 'USER'];
  const lang = langMap[id % 7] || 'HANGUL';

  return { lang, fontName, fontType: 'ttf' };
}

function parseCharShape(data: Uint8Array, id: number): CharProperty | null {
  if (data.length < 72) return null;

  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);

  // 언어별 폰트 참조 (각 2바이트 × 7)
  const fontRef = {
    hangul: readU16(data, 0),
    latin: readU16(data, 2),
    hanja: readU16(data, 4),
    japanese: readU16(data, 6),
    other: readU16(data, 8),
    symbol: readU16(data, 10),
    user: readU16(data, 12),
  };

  // 언어별 글자 크기 (ratio): 14 ~ 27
  // 언어별 자간: 28 ~ 41
  // 언어별 상대크기: 42 ~ 55
  // 언어별 글자위치: 56 ~ 69

  const height = readI32(data, 70); // baseSize (1/100 pt 단위로 인코딩됨 — 실제로 height 값)

  let textColor = 0;
  if (data.length >= 78) {
    textColor = readU32(data, 74);
  }

  let bold = false;
  let italic = false;
  if (data.length >= 74) {
    const attr = readU32(data, 70);
    // 간략하게 bold/italic 추출은 어려움 — HWP5에서는 속성 비트 필드로 관리
  }

  return {
    id,
    height: height > 0 ? height : 1000,
    textColor: textColor & 0xffffff,
    fontRef,
    bold,
    italic,
  };
}

function parseParaShape(data: Uint8Array, id: number): ParaProperty | null {
  if (data.length < 8) return null;

  const attr = readU32(data, 0);
  const alignment = ['JUSTIFY', 'LEFT', 'RIGHT', 'CENTER', 'DISTRIBUTE', 'DISTRIBUTE_SPACE'][attr & 0x07] || 'JUSTIFY';

  let leftMargin = 0, rightMargin = 0, indent = 0;
  if (data.length >= 20) {
    leftMargin = readI32(data, 4);
    rightMargin = readI32(data, 8);
    indent = readI32(data, 12);
  }

  let prevSpacing = 0, nextSpacing = 0;
  if (data.length >= 28) {
    prevSpacing = readI32(data, 16);
    nextSpacing = readI32(data, 20);
  }

  let lineSpacingValue = 160;
  if (data.length >= 32) {
    lineSpacingValue = readI32(data, 24);
  }

  return {
    id,
    alignment: alignment as any,
    lineSpacing: { type: 'PERCENT', value: lineSpacingValue > 0 ? lineSpacingValue : 160 },
    paraMargin: {
      left: Math.max(0, leftMargin),
      right: Math.max(0, rightMargin),
      indent,
      prevSpacing: Math.max(0, prevSpacing),
      nextSpacing: Math.max(0, nextSpacing),
    },
  };
}

function parseStyle(data: Uint8Array, id: number): Style | null {
  if (data.length < 8) return null;

  // 스타일 이름 (UTF-16LE, 첫 2바이트가 길이)
  const nameLen = readU16(data, 0);
  let offset = 2;
  const name = readUtf16(data, offset, nameLen);
  offset += nameLen * 2;

  // 영문 이름
  let engName = '';
  if (offset + 2 <= data.length) {
    const engNameLen = readU16(data, offset);
    offset += 2;
    if (offset + engNameLen * 2 <= data.length) {
      engName = readUtf16(data, offset, engNameLen);
      offset += engNameLen * 2;
    }
  }

  // 속성
  let type: 'PARA' | 'CHAR' = 'PARA';
  let paraPrIDRef = 0;
  let charPrIDRef = 0;
  if (offset + 4 <= data.length) {
    const styleType = data[offset];
    type = styleType === 1 ? 'CHAR' : 'PARA';
    offset += 1;
  }

  return {
    id,
    type,
    name: name || `Style${id}`,
    engName: engName || undefined,
    paraPrIDRef,
    charPrIDRef,
  };
}

function parseBorderFillRecord(data: Uint8Array, id: number): BorderFill | null {
  if (data.length < 2) return null;
  // 간략화: 기본 borderFill 반환
  return createDefaultBorderFill(id);
}

// ─── BodyText 파싱 ───

function parseBodyText(ole: OleFile, compressed: boolean): Section[] {
  const sections: Section[] = [];
  const streams = ole.listStreams();

  // Section0, Section1, ... 스트림 찾기
  const sectionStreams = streams
    .filter((name) => /^Section\d+$/.test(name))
    .sort((a, b) => {
      const numA = parseInt(a.replace('Section', ''), 10);
      const numB = parseInt(b.replace('Section', ''), 10);
      return numA - numB;
    });

  // BodyText 하위 스트림으로도 시도
  if (sectionStreams.length === 0) {
    // OLE 엔트리에서 직접 탐색
    for (const entry of ole.entries) {
      if (/^Section\d+$/.test(entry.name) && entry.type === 2) {
        sectionStreams.push(entry.name);
      }
    }
    sectionStreams.sort();
  }

  for (const streamName of sectionStreams) {
    try {
      const raw = ole.getStream(streamName);
      const sectionData = compressed ? tryDecompress(raw) : raw;
      const records = parseRecords(sectionData);
      sections.push(parseSectionRecords(records));
    } catch {
      // 섹션 파싱 실패 시 빈 섹션
      sections.push({ def: createDefaultSectionDef(), paragraphs: [] });
    }
  }

  if (sections.length === 0) {
    sections.push({ def: createDefaultSectionDef(), paragraphs: [] });
  }

  return sections;
}

function parseSectionRecords(records: HwpRecord[]): Section {
  const paragraphs: Paragraph[] = [];
  let currentParaCharShapeRef = 0;
  let currentParaStyleRef = 0;

  for (let i = 0; i < records.length; i++) {
    const rec = records[i];

    if (rec.tagId === TAG.PARA_HEADER) {
      if (rec.data.length >= 4) {
        // 문단 속성
        const nCharShapeRef = readU32(rec.data, 0);
      }

      // 다음 PARA_TEXT 레코드 찾기
      let textRec: HwpRecord | null = null;
      let charShapeRec: HwpRecord | null = null;
      for (let j = i + 1; j < records.length && records[j].level > rec.level; j++) {
        if (records[j].tagId === TAG.PARA_TEXT && !textRec) {
          textRec = records[j];
        }
        if (records[j].tagId === TAG.PARA_CHAR_SHAPE && !charShapeRec) {
          charShapeRec = records[j];
        }
      }

      const text = textRec ? parseParaText(textRec.data) : '';
      const charPrIDRef = charShapeRec ? readU32(charShapeRec.data, 4) : 0;
      const paraPrIDRef = rec.data.length >= 6 ? readU16(rec.data, 4) : 0;
      const styleIDRef = rec.data.length >= 8 ? readU16(rec.data, 6) : 0;

      paragraphs.push({
        paraPrIDRef,
        styleIDRef,
        runs: [{ t: 'text', text, charPrIDRef }],
      });
    }

    if (rec.tagId === TAG.PAGE_DEF) {
      // 첫 번째 PageDef만 사용
    }
  }

  return {
    def: createDefaultSectionDef(),
    paragraphs,
  };
}

function parseParaText(data: Uint8Array): string {
  let text = '';
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);

  for (let offset = 0; offset + 1 < data.length; offset += 2) {
    const ch = view.getUint16(offset, true);

    // 제어 문자 처리
    if (ch < 0x20) {
      switch (ch) {
        case 0x0d: // paragraph break
        case 0x0a: // line break
          break;
        case 0x09: // tab
          text += '\t';
          break;
        case 1: // reserved
        case 2: // section/column control
        case 3: // field start
          // 확장 제어 문자: 추가 데이터 건너뛰기 (8바이트)
          offset += 14;
          break;
        case 4: // field end
        case 11: // control char (inline)
        case 12: // control char (extended)
        case 15: // hyphen
        case 16: // reserved
        case 17: // reserved
        case 18: // reserved
        case 23: // reserved
        case 24: // inline object
          break;
        default:
          break;
      }
    } else {
      text += String.fromCharCode(ch);
    }
  }

  return text;
}
